// src/pages/billing/order/CreateOrder.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Paper,
  TableContainer,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import BillingLayout from "../../../layouts/BillingLayout";

const BillingCreateOrder = () => {
  const billingUser = JSON.parse(localStorage.getItem("user"));
  const superAdminId = billingUser?.superAdminId;
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    customer: "",
    challanNumber: "",
    date: new Date().toISOString().slice(0, 10),
    items: [
      {
        product: "",
        unit: "",
        availableQty: 0,
        secondaryQuantity: 0,
        quantity: 0,
        price: 0,
        _secFactor: 1,
      },
    ],
  });

  const getTotal = () =>
    form.items.reduce((sum, it) => sum + it.quantity * it.price, 0);

  useEffect(() => {
    async function load() {
      const [cRes, pRes, stockRes, cntRes] = await Promise.all([
        api.get(`/api/customers/get-all/${superAdminId}`),
        api.get(`/api/products/get-all/${superAdminId}`),
        api.get(`/api/reports/closing-stock/${superAdminId}`),
        api.get(`/api/orders/count/${superAdminId}`),
      ]);
      setCustomers(cRes.data);

      const stockMap = {};
      stockRes.data.forEach((s) => {
        stockMap[s.productName] = s.closingQty;
      });
      const prods = pRes.data.map((p) => ({
        ...p,
        closingQty: stockMap[p.productName] || 0,
      }));
      setProducts(prods);

      setForm((f) => ({
        ...f,
        challanNumber: String((cntRes.data.count || 0) + 1).padStart(3, "0"),
      }));
    }
    if (superAdminId) load();
  }, [superAdminId]);

  const handleItemChange = (idx, key, value) => {
    const items = [...form.items];
    const item = { ...items[idx] };

    if (key === "product") {
      const p = products.find((x) => x.productName === value);
      if (p) {
        item.product = p.productName;
        item.unit = p.primaryUnit;
        item.availableQty = p.closingQty;
        item.price = p.minSalePrice || 0;
        item.secondaryQuantity = 0;
        item.quantity = 0;
        item._secFactor = p.secondaryQuantity || 1;
      }
    } else if (key === "secondaryQuantity") {
      item.secondaryQuantity = value;
      item.quantity = parseFloat((value * (item._secFactor || 1)).toFixed(2));
    } else if (key === "quantity") {
      item.quantity = value;
      item.secondaryQuantity = parseFloat(
        (value / (item._secFactor || 1)).toFixed(2)
      );
    } else if (key === "price") {
      item.price = value;
    }

    items[idx] = item;
    setForm((f) => ({ ...f, items }));
  };

  const addRow = () => {
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          product: "",
          unit: "",
          availableQty: 0,
          secondaryQuantity: 0,
          quantity: 0,
          price: 0,
          _secFactor: 1,
        },
      ],
    }));
  };

  const removeRow = (idx) => {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async () => {
    try {
      await api.post("/api/orders/create", {
        ...form,
        superAdminId,
        total: getTotal(),
      });
      alert("Order created successfully");
      navigate("/billing/order/view");
    } catch (err) {
      console.error(err);
      alert("Failed to create order");
    }
  };

  return (
    <BillingLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h5" align="center" gutterBottom>
            Create Order
          </Typography>

          {/* Header Fields */}
          <Box mb={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Customer"
                  value={form.customer}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, customer: e.target.value }))
                  }
                  fullWidth
                >
                  {customers.map((c) => (
                    <MenuItem key={c._id} value={c.partyName}>
                      {c.partyName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Order No"
                  value={form.challanNumber}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, challanNumber: e.target.value }))
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  type="date"
                  label="Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.date}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, date: e.target.value }))
                  }
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {/* Items */}
          {isMobile ? (
            form.items.map((item, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Product"
                      value={item.product}
                      size="small"
                      onChange={(e) =>
                        handleItemChange(idx, "product", e.target.value)
                      }
                      fullWidth
                    >
                      {products.map((p) => (
                        <MenuItem key={p._id} value={p.productName}>
                          {p.productName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Unit"
                      value={item.unit}
                      size="small"
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Avail Stock"
                      value={item.availableQty}
                      size="small"
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Sec Qty"
                      type="number"
                      value={item.secondaryQuantity}
                      size="small"
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          "secondaryQuantity",
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Qty"
                      type="number"
                      value={item.quantity}
                      size="small"
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          "quantity",
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Price"
                      type="number"
                      value={item.price}
                      size="small"
                      onChange={(e) =>
                        handleItemChange(idx, "price", Number(e.target.value))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} container alignItems="center">
                    <Typography>
                      ₹{(item.quantity * item.price).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} textAlign="right">
                    <IconButton onClick={() => removeRow(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableBody>
                  {form.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ width: "12.5%" }}>
                        <TextField
                          select
                          label="Product"
                          value={item.product}
                          size="small"
                          onChange={(e) =>
                            handleItemChange(idx, "product", e.target.value)
                          }
                          fullWidth
                        >
                          {products.map((p) => (
                            <MenuItem key={p._id} value={p.productName}>
                              {p.productName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ width: "12.5%" }}>
                        <TextField
                          label="Unit"
                          value={item.unit}
                          size="small"
                          InputProps={{ readOnly: true }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ width: "12.5%" }}>
                        <TextField
                          label="Avail Stock"
                          value={item.availableQty}
                          size="small"
                          InputProps={{ readOnly: true }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ width: "12.5%" }}>
                        <TextField
                          label="Sec Qty"
                          type="number"
                          value={item.secondaryQuantity}
                          size="small"
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "secondaryQuantity",
                              Number(e.target.value)
                            )
                          }
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ width: "12.5%" }}>
                        <TextField
                          label="Qty"
                          type="number"
                          value={item.quantity}
                          size="small"
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "quantity",
                              Number(e.target.value)
                            )
                          }
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ width: "12.5%" }}>
                        <TextField
                          label="Price"
                          type="number"
                          value={item.price}
                          size="small"
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "price",
                              Number(e.target.value)
                            )
                          }
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ width: "12.5%" }}>
                        ₹{(item.quantity * item.price).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ width: "12.5%" }}>
                        <IconButton onClick={() => removeRow(idx)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Footer */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Button
              variant="outlined"
              onClick={addRow}
              fullWidth={{ xs: true, sm: false }}
            >
              + Add Row
            </Button>
            <Typography variant="h6">
              Total: ₹{getTotal().toFixed(2)}
            </Typography>
          </Box>

          <Box textAlign="center" mt={3}>
            <Button variant="contained" onClick={handleSubmit} size="large">
              Save Order
            </Button>
          </Box>
        </Paper>
      </Container>
    </BillingLayout>
  );
};

export default BillingCreateOrder;
